/**
 * repositories/mfaFactorsRepository.ts
 * MFA Factors table data access layer
 *
 * Architecture: WU1.5 Task 12 - Complete Repository Migration
 *
 * Purpose: Centralize all data access for mfa_factors table.
 * Supplements Supabase Auth's MFA factor management with custom labels and usage tracking.
 *
 * ========================================================================
 * SECURITY NOTE
 * ========================================================================
 *
 * ⚠️ mfa_factors is SECURITY-SENSITIVE. All UPDATE operations
 * require BOTH userId and factorId to prevent cross-user writes.
 *
 * We do NOT expose a "list all factors for this user" method on purpose.
 * MFA enrollment / factor listing should stay in MFAService, which merges
 * Supabase Auth factors + db labels and enforces visibility rules.
 *
 * Do not add broad-scan methods without security review.
 *
 * ========================================================================
 * KEY PRINCIPLES
 * ========================================================================
 *
 * 1. **Composite Key Enforcement**: All writes require (userId, factorId)
 * 2. **No Broad Scans**: No list-all or enumerate methods
 * 3. **Always Project userId**: Include user_id in ALL queries for safety checks
 * 4. **Read-Only Enrichment**: getByFactorIds is for enriching auth factors, not listing
 *
 * ========================================================================
 */

import { supabase } from '@/lib/supabase';
import { toDomain, toPatch, throwIfError } from '@/lib/supabaseMappers';
import type { Database } from '@/types/supabase.types';
import type { MFAFactor, MFAFactorUpdate } from '@/types/supabase/MFAFactor';

// ============================================================================
// WIRE TYPES (snake_case from database)
// ============================================================================

type MFAFactorUpdateRow = Database['public']['Tables']['mfa_factors']['Update'];

// ============================================================================
// PROJECTION CONSTANTS
// ============================================================================

/**
 * Standard projection for mfa_factors
 * ALWAYS includes user_id for safety checks, even in enrichment queries
 */
const MFA_FACTORS_PROJECTION = 'user_id,factor_id,label,last_used_at,created_at' as const;

// ============================================================================
// REPOSITORY
// ============================================================================

/**
 * MFA Factors Repository
 *
 * Provides data access for mfa_factors table.
 * All methods return camelCase domain types.
 *
 * SECURITY: All update methods require (userId, factorId) to prevent cross-user writes.
 */
export const mfaFactorsRepository = {
  /**
   * Get MFA factors by factor IDs (for enrichment)
   *
   * Used by MFAService to enrich Supabase Auth factors with custom labels and usage data.
   *
   * SECURITY: Always returns userId in results. Service should verify factors belong to current user.
   *
   * @param factorIds - Array of factor IDs to fetch
   * @returns Promise resolving to array of MFA factors
   *
   * @example
   * const factors = await mfaFactorsRepository.getByFactorIds(['factor1', 'factor2']);
   * // Service should verify: factors.every(f => f.userId === currentUser.id)
   */
  async getByFactorIds(factorIds: string[]): Promise<MFAFactor[]> {
    if (factorIds.length === 0) {
      return [];
    }

    const result = await supabase
      .from('mfa_factors')
      .select(MFA_FACTORS_PROJECTION)
      .in('factor_id', factorIds);

    const data = throwIfError(result);
    return (data || []).map(row => toDomain<MFAFactor>(row));
  },

  /**
   * Update custom label for MFA factor
   *
   * SECURITY: Requires BOTH userId and factorId to prevent cross-user updates.
   *
   * @param userId - User ID (for security enforcement)
   * @param factorId - Factor ID to update
   * @param label - New custom label (user-friendly name)
   * @returns Promise resolving when update completes
   *
   * @example
   * await mfaFactorsRepository.updateLabel(userId, factorId, 'My Phone');
   */
  async updateLabel(userId: string, factorId: string, label: string): Promise<void> {
    const updateData: MFAFactorUpdate = { label };
    const patchData = toPatch<MFAFactorUpdateRow>(updateData);

    const result = await supabase
      .from('mfa_factors')
      .update(patchData)
      .eq('user_id', userId)    // SECURITY: Enforce user ownership
      .eq('factor_id', factorId);

    throwIfError(result);
  },

  /**
   * Update last used timestamp for MFA factor
   *
   * Called after successful MFA challenge to track factor usage.
   *
   * SECURITY: Requires BOTH userId and factorId to prevent cross-user updates.
   *
   * @param userId - User ID (for security enforcement)
   * @param factorId - Factor ID to update
   * @param timestampIso - Last used timestamp (ISO 8601 format)
   * @returns Promise resolving when update completes
   *
   * @example
   * await mfaFactorsRepository.updateLastUsedAt(userId, factorId, new Date().toISOString());
   */
  async updateLastUsedAt(userId: string, factorId: string, timestampIso: string): Promise<void> {
    const updateData: MFAFactorUpdate = { lastUsedAt: timestampIso };
    const patchData = toPatch<MFAFactorUpdateRow>(updateData);

    const result = await supabase
      .from('mfa_factors')
      .update(patchData)
      .eq('user_id', userId)    // SECURITY: Enforce user ownership
      .eq('factor_id', factorId);

    throwIfError(result);
  }
};
